<template>
  <div class="product-page">
    <section class="p-hero reveal">
      <SectionBackdrop :bg="bgHero" variant="light" :count="32" />
      <div class="container p-hero-inner">
        <div class="p-hero-copy">
          <div class="p-hero-badge">
            <img :src="logo" :alt="name" />
            <span>{{ name }}</span>
          </div>
          <h1>{{ headline || name }}</h1>
          <p class="p-hero-lead">{{ lead }}</p>
          <div class="p-hero-features">
            <p v-for="line in featureLines" :key="line" class="p-hero-feature">{{ line }}</p>
          </div>
          <div class="p-hero-actions">
            <a :href="downloadLink" :download="downloadFileName" class="btn-download">
              <img src="@/assets/videocvt/Windows.png" alt="Windows" class="btn-os-icon" />
              Free download
            </a>
          </div>
          <p class="p-hero-system">Supported OS: Windows 8 / 10 / 11</p>
          <div class="p-hero-meta">
            <span v-for="item in metaItems" :key="item.label" class="p-hero-meta-item">
              <span class="meta-icon">
                <FeatureIcon :name="item.icon" :alt="item.label" size="sm" />
              </span>
              {{ item.label }}
            </span>
          </div>
        </div>
        <div class="p-hero-visual shot-glow">
          <img :src="heroImage" :alt="`${name} preview`" class="app-shot" loading="lazy" />
        </div>
      </div>
    </section>

    <section class="p-features">
      <SectionBackdrop :bg="bgFeatures" variant="light" :count="20" />
      <div class="container">
        <div class="p-features-head reveal">
          <p class="overline">Features</p>
          <h2>{{ featuresTitle }}</h2>
          <p>{{ featuresDesc }}</p>
        </div>

        <article
          v-for="(item, index) in detailedFeatures"
          :key="item.title"
          class="p-feature-row reveal"
          :class="{ 'p-feature-row--reverse': index % 2 === 1 }"
        >
          <div class="p-feature-visual shot-glow">
            <img :src="item.image" :alt="item.title" class="app-shot" loading="lazy" />
          </div>
          <div class="p-feature-copy">
            <h3>{{ item.title }}</h3>
            <p class="p-feature-brief">{{ item.brief }}</p>
            <p class="p-feature-detail">{{ item.detail }}</p>
            <a :href="downloadLink" :download="downloadFileName" class="btn-download btn-download-sm">
              <img src="@/assets/videocvt/Windows.png" alt="Windows" class="btn-os-icon" />
              Free download
            </a>
          </div>
        </article>

        <div class="p-other reveal">
          <div class="p-other-head">
            <h3>{{ otherTitle }}</h3>
            <p>{{ otherDesc }}</p>
          </div>
          <div class="p-other-grid">
            <article v-for="item in otherFeatures" :key="item.title" class="p-other-card">
              <div class="p-other-icon">
                <img v-if="item.iconImage" :src="item.iconImage" :alt="item.title" class="p-other-img-icon" />
                <FeatureIcon v-else :name="item.icon" :alt="item.title" size="md" />
              </div>
              <div>
                <h4>{{ item.title }}</h4>
                <p>{{ item.description }}</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="p-reviews reveal">
      <SectionBackdrop :bg="bgReviews" variant="dark" :count="30" />
      <div class="container">
        <div class="p-reviews-head">
          <h3>{{ reviewsTitle }}</h3>
          <p>{{ reviewsDesc }}</p>
        </div>
        <div class="p-reviews-viewport">
          <div class="p-reviews-track" :style="{ transform: `translateX(-${activeReviewGroup * 100}%)` }">
            <div v-for="(group, gi) in reviewGroups" :key="`g-${gi}`" class="p-review-group">
              <article v-for="item in group" :key="item.name" class="p-review-card">
                <div class="p-review-user">
                  <div class="p-review-avatar">
                    <img :src="item.avatar" :alt="`${item.name} avatar`" />
                  </div>
                  <div>
                    <div class="p-review-name">{{ item.name }}</div>
                    <div class="p-review-stars" aria-label="5 stars">
                      <span v-for="n in 5" :key="n">★</span>
                    </div>
                  </div>
                </div>
                <p class="p-review-text">{{ item.comment }}</p>
              </article>
            </div>
          </div>
        </div>
        <div class="p-review-dots">
          <button
            v-for="(group, index) in reviewGroups"
            :key="`dot-${index}`"
            class="p-review-dot"
            :class="{ active: activeReviewGroup === index }"
            @click="activeReviewGroup = index"
          ></button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import FeatureIcon from '@/components/FeatureIcon.vue'
import SectionBackdrop from '@/components/SectionBackdrop.vue'

export default {
  name: 'ProductPageLayout',
  components: { FeatureIcon, SectionBackdrop },
  props: {
    name: { type: String, required: true },
    headline: { type: String, default: '' },
    logo: { type: String, required: true },
    heroImage: { type: String, required: true },
    lead: { type: String, required: true },
    featureLines: { type: Array, default: () => [] },
    downloadLink: { type: String, required: true },
    downloadFileName: { type: String, required: true },
    metaItems: { type: Array, default: () => [] },
    detailedFeatures: { type: Array, default: () => [] },
    otherFeatures: { type: Array, default: () => [] },
    reviews: { type: Array, default: () => [] },
    featuresTitle: { type: String, default: 'Core features' },
    featuresDesc: { type: String, default: 'Real screenshots that highlight what matters most.' },
    otherTitle: { type: String, default: 'More capabilities' },
    otherDesc: { type: String, default: 'Extra tools for everyday workflows.' },
    reviewsTitle: { type: String, default: 'What users say' },
    reviewsDesc: { type: String, default: 'Feedback from people who use the app every day.' }
  },
  data() {
    return {
      observer: null,
      reviewTimer: null,
      activeReviewGroup: 0,
      bgHero: require('@/assets/illustrations/bg-product-hero.svg'),
      bgFeatures: require('@/assets/illustrations/bg-product-features.svg'),
      bgReviews: require('@/assets/illustrations/bg-standards-glow.svg')
    }
  },
  computed: {
    reviewGroups() {
      return [this.reviews.slice(0, 3), this.reviews.slice(3, 6)]
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 }
    )
    this.$el.querySelectorAll('.reveal').forEach(el => this.observer.observe(el))
    this.reviewTimer = setInterval(() => {
      this.activeReviewGroup = this.activeReviewGroup === 0 ? 1 : 0
    }, 5000)
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
    if (this.reviewTimer) clearInterval(this.reviewTimer)
  }
}
</script>

<style scoped>
.p-other-img-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
</style>
